import React, { useState, useEffect } from 'react';
import { UserProfile, AddTeamMemberRequest } from '../utils/types';
import { apiGet, apiPost, ApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import LocationSelector from './LocationSelector';

interface AddTeamMemberProps {
  teamId: string;
  onMemberAdded: () => void;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  // Join team functionality
  canJoinTeam?: boolean;
  checkingTeamStatus?: boolean;
  joining?: boolean;
  lookingForMembers?: boolean;
  onJoinTeam?: () => void;
  teamFull?: boolean;
  teamCapacity?: number;
  currentMemberCount?: number;
  // Permission to add other members
  canAddOthers?: boolean;
}

const AddTeamMember: React.FC<AddTeamMemberProps> = ({ 
  teamId, 
  onMemberAdded, 
  onError, 
  onSuccess,
  canJoinTeam = false,
  checkingTeamStatus = false,
  joining = false,
  lookingForMembers = false,
  onJoinTeam,
  teamFull = false,
  teamCapacity = 5,
  currentMemberCount = 0,
  canAddOthers = false
}) => {
  const [availableUsers, setAvailableUsers] = useState<UserProfile[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<UserProfile[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [showUserList, setShowUserList] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [memberError, setMemberError] = useState('');
  const [mode, setMode] = useState<'existing' | 'external' | 'join'>(() => {
    // Set default mode based on available permissions
    if (canJoinTeam) return 'join';
    if (canAddOthers) return 'existing';
    return 'existing'; // fallback
  });
  
  // Update mode when permissions change (e.g., after joining team)
  useEffect(() => {
    // Check if current mode is no longer valid
    if (mode === 'join' && !canJoinTeam) {
      // User joined team, switch to existing users mode if they can add others
      if (canAddOthers) {
        console.log('🔄 AddTeamMember: Switching from join to existing mode after joining team');
        setMode('existing');
      }
    } else if ((mode === 'existing' || mode === 'external') && !canAddOthers) {
      // User lost permission to add others, switch to join if they can
      if (canJoinTeam) {
        console.log('🔄 AddTeamMember: Switching to join mode due to permission change');
        setMode('join');
      }
    }
  }, [canJoinTeam, canAddOthers, mode]);
  
  // External user form fields
  const [externalName, setExternalName] = useState('');
  const [externalEmail, setExternalEmail] = useState('');
  const [externalLocation, setExternalLocation] = useState('');
  const [externalLocationOther, setExternalLocationOther] = useState('');
  
  // Existing user location selection modal
  const [showExistingUserLocationSelector, setShowExistingUserLocationSelector] = useState(false);
  const [existingUserLocation, setExistingUserLocation] = useState('');
  const [existingUserLocationOther, setExistingUserLocationOther] = useState('');
  const [pendingExistingUser, setPendingExistingUser] = useState<UserProfile | null>(null);
  
  const { token } = useAuth();
  const canManageAdditions = canAddOthers && !teamFull;

  useEffect(() => {
    if (token) {
      loadAvailableUsers();
    }
  }, [token]);

  useEffect(() => {
    // Only search if we have a search term AND no user is selected
    // This prevents re-triggering search when a user is selected
    if (searchTerm && searchTerm.length >= 2 && !selectedUser) {
      console.log('🔍 AddTeamMember: Triggering search for:', searchTerm);
      // Use direct search when user types
      searchUsers(searchTerm);
    } else if (!selectedUser) {
      // Only clear if no user is selected
      console.log('🔍 AddTeamMember: Clearing search results');
      setFilteredUsers([]);
      setShowUserList(false);
    } else {
      console.log('🔍 AddTeamMember: User selected, not searching');
    }
  }, [searchTerm, selectedUser]);

  const searchUsers = async (query: string) => {
    if (!token || !query.trim() || query.length < 2) return;
    
    try {
      // Use the new Azure AD search endpoint
      const users = await apiGet<UserProfile[]>(`/users/search?q=${encodeURIComponent(query.trim())}`, token);
      setFilteredUsers(users);
      setShowUserList(true);
    } catch (err) {
      // If Azure AD search fails, fall back to local search
      console.warn('Azure AD search failed, using local search:', err);
      const filtered = availableUsers.filter(user => 
        user.display_name.toLowerCase().includes(query.toLowerCase()) ||
        user.email.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredUsers(filtered);
      setShowUserList(true);
    }
  };

  const loadAvailableUsers = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      // Load initial set of local users as fallback
      const users = await apiGet<UserProfile[]>('/users/available', token);
      setAvailableUsers(users);
    } catch (err) {
      if (err instanceof ApiError) {
        onError(`Failed to load users: ${err.message}`);
      } else {
        onError('Failed to load available users');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (user: UserProfile) => {
    console.log('🔍 AddTeamMember: User selected:', user.display_name);
    setSelectedUser(user);
    setSearchTerm(user.display_name);
    setShowUserList(false);
  };

  const handleAddMember = async () => {
    if (!token) return;
    setMemberError('');

    // Validate based on mode
    if (mode === 'existing') {
      if (!selectedUser) return;
      
      // For existing users, show location selector modal
      setPendingExistingUser(selectedUser);
      setShowExistingUserLocationSelector(true);
      return;
    } else {
      if (!externalName.trim() || !externalEmail.trim()) {
        const message = 'Please fill in both name and email for external user';
        setMemberError(message);
        onError(message);
        return;
      }
      
      // Validate location for external users
      if (!externalLocation) {
        const message = 'Please select a location for the external user';
        setMemberError(message);
        onError(message);
        return;
      }
      
      if (externalLocation === 'Other' && !externalLocationOther.trim()) {
        const message = 'Please specify the location when selecting "Other"';
        setMemberError(message);
        onError(message);
        return;
      }
      
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(externalEmail.trim())) {
        const message = 'Please enter a valid email address';
        setMemberError(message);
        onError(message);
        return;
      }
    }

    // This will only be reached for external users now
    await submitMemberRequest();
  };

  const submitMemberRequest = async (userLocation?: string, userLocationOther?: string) => {
    if (!token) return;

    setMemberError('');
    setAdding(true);
    try {
      const request: AddTeamMemberRequest = mode === 'existing' ? {
        user_id: pendingExistingUser!.user_id,
        email: pendingExistingUser!.email,
        display_name: pendingExistingUser!.display_name,
        location: userLocation || existingUserLocation,
        location_other: userLocationOther || existingUserLocationOther
      } : {
        // External user - no user_id
        email: externalEmail.trim(),
        display_name: externalName.trim(),
        location: externalLocation,
        location_other: externalLocationOther
      };

      await apiPost(`/teams/${teamId}/members`, request, token);
      
      const memberName = mode === 'existing' ? (pendingExistingUser?.display_name || selectedUser?.display_name) : externalName.trim();
      onSuccess(`Added ${memberName} to the team successfully!`);
      onMemberAdded();
      setMemberError('');
      
      // Reset form
      if (mode === 'existing') {
        setSelectedUser(null);
        setSearchTerm('');
        setShowUserList(false);
        setPendingExistingUser(null);
        setExistingUserLocation('');
        setExistingUserLocationOther('');
        setShowExistingUserLocationSelector(false);
      } else {
        setExternalName('');
        setExternalEmail('');
        setExternalLocation('');
        setExternalLocationOther('');
      }
      
    } catch (err) {
      let message = err instanceof ApiError
        ? `Failed to add member: ${err.message}`
        : 'Failed to add team member';

      if (err instanceof ApiError && err.status === 400 && teamFull) {
        message = `Failed to add member: Team is full at ${currentMemberCount}/${teamCapacity} members.`;
      }

      setMemberError(message);
      onError(message);
    } finally {
      setAdding(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedUser(null);
  };

  const clearSelection = () => {
    setSelectedUser(null);
    setSearchTerm('');
    setShowUserList(false);
  };

  // Handler for confirming existing user location selection
  const handleConfirmExistingUserLocation = () => {
    if (!existingUserLocation) {
      const message = 'Please select a location';
      setMemberError(message);
      onError(message);
      return;
    }
    
    if (existingUserLocation === 'Other' && !existingUserLocationOther.trim()) {
      const message = 'Please specify the location when selecting "Other"';
      setMemberError(message);
      onError(message);
      return;
    }
    
    // Submit the member request with the selected location
    submitMemberRequest(existingUserLocation, existingUserLocationOther);
  };

  // Handler for canceling existing user location selection
  const handleCancelExistingUserLocation = () => {
    setShowExistingUserLocationSelector(false);
    setPendingExistingUser(null);
    setExistingUserLocation('');
    setExistingUserLocationOther('');
    setMemberError('');
  };

  if (loading) {
    return (
      <div className="add-member-container" style={{ marginBottom: '20px' }}>
        <h4>➕ Add Team Member</h4>
        <div>Loading available users...</div>
      </div>
    );
  }

  return (
    <div className="add-member-container" style={{ marginBottom: '20px' }}>
      <h4>👥 Team Member Management</h4>
      <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
        Use the tabs below to search for users from your organization, add external users, or join the team yourself.
      </p>

      {teamFull && (
        <div style={{
          marginBottom: '15px',
          padding: '12px 14px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '4px',
          color: '#856404'
        }}>
          This team is full at {currentMemberCount}/{teamCapacity} members. Remove a member to add someone new.
        </div>
      )}

      {memberError && (
        <div style={{
          marginBottom: '15px',
          padding: '10px',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          color: '#721c24'
        }}>
          {memberError}
        </div>
      )}
      
      {/* Mode Toggle */}
      {canManageAdditions && (
      <div style={{ marginBottom: '20px' }}>
        {/* Tab Header */}
        <div style={{ 
          borderBottom: '2px solid #dee2e6', 
          marginBottom: '15px',
          paddingBottom: '2px'
        }}>
          <div style={{ display: 'flex', gap: '0', flexWrap: 'wrap' }}>
            {canAddOthers && (
              <button
                onClick={() => setMode('existing')}
                style={{
                  padding: '10px 16px',
                  backgroundColor: mode === 'existing' ? 'white' : 'transparent',
                  color: mode === 'existing' ? '#007bff' : '#666',
                  border: 'none',
                  borderBottom: mode === 'existing' ? '2px solid #007bff' : '2px solid transparent',
                  borderRadius: '4px 4px 0 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: mode === 'existing' ? 'bold' : 'normal',
                  marginBottom: '-2px'
                }}
              >
                🔍 Search Azure AD Users
              </button>
            )}
            {canAddOthers && (
              <button
                onClick={() => setMode('external')}
                style={{
                  padding: '10px 16px',
                  backgroundColor: mode === 'external' ? 'white' : 'transparent',
                  color: mode === 'external' ? '#007bff' : '#666',
                  border: 'none',
                  borderBottom: mode === 'external' ? '2px solid #007bff' : '2px solid transparent',
                  borderRadius: '4px 4px 0 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: mode === 'external' ? 'bold' : 'normal',
                  marginBottom: '-2px'
                }}
              >
                👤 Add External User
              </button>
            )}
            {canJoinTeam && (
              <button
                onClick={() => setMode('join')}
                style={{
                  padding: '10px 16px',
                  backgroundColor: mode === 'join' ? 'white' : 'transparent',
                  color: mode === 'join' ? '#007bff' : '#666',
                  border: 'none',
                  borderBottom: mode === 'join' ? '2px solid #007bff' : '2px solid transparent',
                  borderRadius: '4px 4px 0 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: mode === 'join' ? 'bold' : 'normal',
                  marginBottom: '-2px'
                }}
              >
                {lookingForMembers ? '🚀 Join This Team' : '🤝 Join Team'}
              </button>
            )}
          </div>
        </div>
      </div>
      )}

      {mode === 'existing' && canManageAdditions && (
        // Existing Users Mode
        <div style={{ position: 'relative', marginBottom: '15px' }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <input
                type="text"
                placeholder="Type to search Azure AD users (name, email)..."
                value={searchTerm}
                onChange={handleSearchChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                onFocus={() => {
                  if (searchTerm && filteredUsers.length > 0 && !selectedUser) {
                    setShowUserList(true);
                  }
                }}
                minLength={2}
              />
              
              {/* Clear button */}
              {(searchTerm || selectedUser) && (
                <button
                  onClick={clearSelection}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    fontSize: '16px',
                    cursor: 'pointer',
                    color: '#999'
                  }}
                >
                  ✕
                </button>
              )}
              
              {/* User dropdown */}
              {showUserList && filteredUsers.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderTop: 'none',
                  borderRadius: '0 0 4px 4px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  {filteredUsers.map((user) => (
                    <div
                      key={user.user_id}
                      onClick={() => handleUserSelect(user)}
                      style={{
                        padding: '10px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee',
                        backgroundColor: 'white'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                      }}
                    >
                      <div style={{ fontWeight: 'bold' }}>{user.display_name}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{user.email}</div>
                      {user.skills && user.skills.length > 0 && (
                        <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                          Skills: {user.skills.slice(0, 3).join(', ')}
                          {user.skills.length > 3 && '...'}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <button
              onClick={handleAddMember}
              disabled={!selectedUser || adding}
              style={{
                padding: '10px 20px',
                backgroundColor: selectedUser ? '#007bff' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: selectedUser && !adding ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              {adding ? 'Adding...' : 'Add Member'}
            </button>
          </div>
          
          {/* Selected user display */}
          {selectedUser && (
            <div style={{
              marginTop: '10px',
              padding: '10px',
              backgroundColor: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: '4px'
            }}>
              <div style={{ fontWeight: 'bold' }}>Selected: {selectedUser.display_name}</div>
              <div style={{ fontSize: '14px', color: '#666' }}>{selectedUser.email}</div>
              {selectedUser.skills && selectedUser.skills.length > 0 && (
                <div style={{ marginTop: '5px' }}>
                  {selectedUser.skills.map((skill, index) => (
                    <span key={index} style={{
                      display: 'inline-block',
                      backgroundColor: '#28a745',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '11px',
                      marginRight: '5px',
                      marginTop: '2px'
                    }}>
                      {skill}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* No users found message */}
          {searchTerm && searchTerm.length >= 2 && filteredUsers.length === 0 && (
            <div style={{
              marginTop: '10px',
              padding: '10px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '4px',
              color: '#856404'
            }}>
              No users found matching "{searchTerm}". Try a different search term or check spelling.
            </div>
          )}
          
          {/* Search prompt when term is too short */}
          {searchTerm && searchTerm.length < 2 && (
            <div style={{
              marginTop: '10px',
              padding: '10px',
              backgroundColor: '#e7f3ff',
              border: '1px solid #b3d7ff',
              borderRadius: '4px',
              color: '#0056b3',
              fontSize: '12px'
            }}>
              💡 Type at least 2 characters to search Azure AD users
            </div>
          )}

          {/* No available users message */}
          {!searchTerm && availableUsers.length === 0 && !loading && (
            <div style={{
              marginTop: '10px',
              padding: '10px',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              color: '#721c24'
            }}>
              No users available. Use the search above to find Azure AD users or add external users.
            </div>
          )}
        </div>
      )}
      
      {mode === 'external' && canManageAdditions && (
        // External User Mode
        <div style={{ marginBottom: '15px' }}>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
            Add external users (e.g. students) who don't have system accounts.
            They won't be able to log in but will appear in your team member list.
          </p>
          
          <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold' }}>
                Full Name *
              </label>
              <input
                type="text"
                placeholder="e.g., John Smith"
                value={externalName}
                onChange={(e) => setExternalName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold' }}>
                Email Address *
              </label>
              <input
                type="email"
                placeholder="e.g., john.smith@university.edu"
                value={externalEmail}
                onChange={(e) => setExternalEmail(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>
          
          <LocationSelector
            location={externalLocation}
            locationOther={externalLocationOther}
            onLocationChange={setExternalLocation}
            onLocationOtherChange={setExternalLocationOther}
            required={true}
            disabled={adding}
          />
          
          <button
            onClick={handleAddMember}
            disabled={!externalName.trim() || !externalEmail.trim() || !externalLocation || adding}
            style={{
              padding: '10px 20px',
              backgroundColor: (externalName.trim() && externalEmail.trim() && externalLocation) ? '#28a745' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (externalName.trim() && externalEmail.trim() && externalLocation && !adding) ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {adding ? 'Adding...' : '+ Add External User'}
          </button>
        </div>
      )}
      
      {mode === 'join' && canJoinTeam && (
        // Join Team Mode
        <div style={{ marginBottom: '15px' }}>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
            Join this team as a member to collaborate with other team members.
          </p>
          
          {checkingTeamStatus ? (
            <div style={{ color: '#666' }}>🔍 Checking team eligibility...</div>
          ) : (
            <button
              onClick={onJoinTeam}
              disabled={joining}
              style={{
                background: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '12px 24px',
                fontSize: '16px',
                cursor: joining ? 'not-allowed' : 'pointer',
                opacity: joining ? 0.6 : 1,
                fontWeight: 'bold'
              }}
            >
              {joining ? '⏳ Joining...' : (
                lookingForMembers ? '🚀 Join This Team' : '🤝 Join Team'
              )}
            </button>
          )}
        </div>
      )}
      
      {/* No permissions message */}
      {!canAddOthers && !canJoinTeam && (
        <div style={{
          marginBottom: '15px',
          padding: '15px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          color: '#6c757d',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '16px', marginBottom: '5px' }}>👥</div>
          <div>
            {teamFull
              ? `This team is full at ${currentMemberCount}/${teamCapacity} members.`
              : 'You need to be a team member or organizer to manage team membership.'}
          </div>
        </div>
      )}

      {/* Location Selection Modal for Existing Users */}
      {showExistingUserLocationSelector && pendingExistingUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            minWidth: '400px',
            maxWidth: '500px'
          }}>
            <h3 style={{ 
              margin: '0 0 20px 0',
              color: '#333',
              fontSize: '20px'
            }}>
              Select Location for {pendingExistingUser.display_name}
            </h3>
            <p style={{ 
              color: '#666', 
              marginBottom: '20px',
              lineHeight: '1.5'
            }}>
              Please select {pendingExistingUser.display_name}'s location for merch and catering ordering:
            </p>

            {memberError && (
              <div style={{
                marginBottom: '15px',
                padding: '10px',
                backgroundColor: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '4px',
                color: '#721c24'
              }}>
                {memberError}
              </div>
            )}
            
            <LocationSelector
              location={existingUserLocation}
              locationOther={existingUserLocationOther}
              onLocationChange={setExistingUserLocation}
              onLocationOtherChange={setExistingUserLocationOther}
              required={true}
            />
            
            <div style={{ 
              display: 'flex', 
              gap: '10px', 
              marginTop: '25px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleCancelExistingUserLocation}
                style={{
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmExistingUserLocation}
                disabled={adding}
                style={{
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  cursor: adding ? 'not-allowed' : 'pointer',
                  opacity: adding ? 0.6 : 1
                }}
              >
                {adding ? '⏳ Adding...' : 'Add Member'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddTeamMember;
